import React, {useRef} from 'react';
import clsx from 'clsx';
import scrollIntoView from 'scroll-into-view-if-needed';
import {Icon, faAngleDown} from '@netx/core-icons';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	accordionSummaryClasses,
	Typography,
} from '@mui/material';
import {useHash} from '@netx/core-components/hooks/useHash';
import {MenuList} from '@netx/core-components/MenuList';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {isRouteMatch} from '@netx/core-next/utils/route';
import {
	ACTION_MENU_LIST_ITEM_ACCORDION_CLASS_NAME,
	actionMenuListItemAccordionClasses,
	type ActionMenuListItemAccordionClasses,
	getActionMenuListItemAccordionUtilityClass,
} from './actionMenuListItemAccordionClasses';
import type {ActionMenuItemBaseProps} from './types';

type ActionMenuListItemAccordionOwnerState =
	WithClasses<ActionMenuListItemAccordionClasses>;

const useUtilityClasses = (state: ActionMenuListItemAccordionOwnerState) => {
	const {classes} = state;
	const slots = {
		root: ['root'],
	};
	return composeClasses(
		slots,
		getActionMenuListItemAccordionUtilityClass,
		classes,
	);
};

const ActionMenuMobileListItemAccordionRoot = styled(Accordion, {
	name: ACTION_MENU_LIST_ITEM_ACCORDION_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	boxShadow: 'none',
	minWidth: '100%',
	'&::before': {
		display: 'none',
	},
	[`&.${accordionSummaryClasses.expanded}`]: {
		margin: 0,
	},
}));

const ActionMenuMobileListItemAccordionDetails = styled(AccordionDetails, {
	name: ACTION_MENU_LIST_ITEM_ACCORDION_CLASS_NAME,
	slot: 'Details',
})(({theme}) => ({
	background: theme.palette.custom.background.accent,
	padding: 0,
}));

const ActionMenuMobileListItemAccordionSummary = styled(AccordionSummary, {
	name: ACTION_MENU_LIST_ITEM_ACCORDION_CLASS_NAME,
	slot: 'Summary',
})(({theme}) => ({
	color: theme.palette.text.primary,
	'&:hover': {
		backgroundColor: theme.palette.action.hover,
	},
	'& .MuiAccordionSummary-expandIconWrapper': {
		color: 'inherit',
		fontSize: 16,
	},
	minHeight: 36,
	'& .MuiAccordionSummary-content': {
		margin: 0,
	},
	[theme.breakpoints.down('sm')]: {
		minHeight: 48,
	},
}));

const ActionMenuMobileListItemIcon = styled('div')(({theme}) => ({
	justifyContent: 'center',
	marginRight: 8,
	marginLeft: -4,
	display: 'flex',
	alignItems: 'center',
	color: 'inherit',
	// TODO: this is repeated in multiple places
	// need to see about getting it under one roof - possibly under the overrides in mui theme file
	// It is just too darn wide
	minWidth: 36,
	'& svg': {
		fontSize: 16,
	},
}));

export interface ActionMenuListItemAccordionProps
	extends
		WithClasses<ActionMenuListItemAccordionClasses>,
		ActionMenuItemBaseProps {
	renderAction: (
		props: ActionMenuItemBaseProps & {
			selected?: boolean;
		},
	) => React.ReactNode;
}

export const ActionMenuListItemAccordion = ({
	classes: _classes,
	className,
	action,
	disableIcons,
	indentSize = 50,
	level = 0,
	someSelected,
	onAction,
	renderAction,
	...props
}: ActionMenuListItemAccordionProps) => {
	const hash = useHash();
	const rootRef = useRef<HTMLDivElement>(null);

	const ownerState: ActionMenuListItemAccordionOwnerState = {
		classes: _classes,
		className,
	};
	const classes = useUtilityClasses(ownerState);

	if (!action.children || !action.children.length) return null;

	const flattenedChildren = action.children.flat();
	const matchedRoutes = flattenedChildren.map((childAction) =>
		isRouteMatch(hash.current, childAction.routeMatch),
	);
	// const someRoutesMatch = matchedRoutes.some(Boolean);

	return (
		<ActionMenuMobileListItemAccordionRoot
			className={clsx(classes.root, className)}
			key={`action-menu-item-accordion-${action.name}`}
			// defaultExpanded={someRoutesMatch}
			disableGutters
			ref={rootRef}
			style={{
				background: !level ? undefined : 'transparent',
			}}
			TransitionProps={{
				onEntered: () => {
					rootRef.current &&
						scrollIntoView(rootRef.current, {
							scrollMode: 'if-needed',
							behavior: 'smooth',
							block: 'start',
						});
				},
			}}
		>
			<ActionMenuMobileListItemAccordionSummary
				expandIcon={<Icon icon={faAngleDown} />}
				style={{paddingLeft: level ? level * indentSize : undefined}}
			>
				{action.icon && (
					<ActionMenuMobileListItemIcon>
						<Icon icon={action.icon} />
					</ActionMenuMobileListItemIcon>
				)}
				<Typography>{action.label}</Typography>
			</ActionMenuMobileListItemAccordionSummary>

			<ActionMenuMobileListItemAccordionDetails>
				<MenuList>
					{flattenedChildren.map((childAction, index) =>
						renderAction({
							key: `sub-action-menu-item-${childAction.name}-${index}`,
							action: childAction,
							disableIcons,
							indentSize,
							level: level + 1,
							selected: matchedRoutes[index] || childAction.selected,
							someSelected,
							onAction,
							...props,
						}),
					)}
				</MenuList>
			</ActionMenuMobileListItemAccordionDetails>
		</ActionMenuMobileListItemAccordionRoot>
	);
};
